import { isNullOrUndefined } from '../../helpers/helpers';

const PROJECT_RATE_FIELDS = [
  { 
    label: 'Regular Billing Rate',
    value: 'regularBillingRate',
    type: 'labour',
    rateType: 'billing',
    abbrev: 'Regular'
  },
  {
    label: 'Overtime Billing Rate',
    value: 'otBillingRate',
    type: 'labour',
    rateType: 'billing',
    abbrev: 'OT'
  },
  {
    label: 'Double Time Billing Rate',
    value: 'dtBillingRate',
    type: 'labour',
    rateType: 'billing',
    abbrev: 'DT'
  },
  {
    label: 'Regular Cost Rate',
    value: 'regularCostRate',
    type: 'labour',
    rateType: 'cost',
    abbrev: 'Regular'
  },
  {
    label: 'Overtime Cost Rate',
    value: 'otCostRate',
    type: 'labour',
    rateType: 'cost',
    abbrev: 'OT'
  },
  {
    label: 'Double Time Cost Rate',
    value: 'dtCostRate',
    type: 'labour',
    rateType: 'cost',
    abbrev: 'DT'
  },
  {
    label: 'Hourly Billing Rate',
    value: 'hourlyBillingRate',
    type: 'equipment',
    rateType: 'billing',
    abbrev: 'Hourly'
  },
  {
    label: 'Daily Billing Rate',
    value: 'dailyBillingRate',
    type: 'equipment',
    rateType: 'billing',
    abbrev: 'Daily'
  },
  {
    label: 'Weekly Billing Rate',
    value: 'weeklyBillingRate',
    type: 'equipment',
    rateType: 'billing',
    abbrev: 'Weekly'
  },
  {
    label: 'Monthly Billing Rate',
    value: 'monthlyBillingRate',
    type: 'equipment',
    rateType: 'billing',
    abbrev: 'Monthly'
  },
  {
    label: 'Hourly Cost Rate',
    value: 'hourlyCostRate',
    type: 'equipment',
    rateType: 'cost',
    abbrev: 'Hourly'
  },
  {
    label: 'Daily Cost Rate',
    value: 'dailyCostRate',
    type: 'equipment',
    rateType: 'cost',
    abbrev: 'Daily'
  },
  {
    label: 'Weekly Cost Rate',
    value: 'weeklyCostRate',
    type: 'equipment',
    rateType: 'cost',
    abbrev: 'Weekly'
  },
  {
    label: 'Monthly Cost Rate',
    value: 'monthlyCostRate',
    type: 'equipment',
    rateType: 'cost',
    abbrev: 'Monthly'
  },
];

export const getProjectRateFields = ({ type = null, rateType = null }) => {
  let fields = PROJECT_RATE_FIELDS;
  if (type) {
    fields = fields.filter((f) => f.type === type);
  }
  if (rateType) {
    fields = fields.filter((f) => f.rateType === rateType);
  }
  return fields;
}

export function billingTypeDisplayName(type) {
  return type === 'labour' ? 'Labour' : 'Equipment';
}

export function formatBillingRate(rate) {
  const rateNum = Number(rate);
  if (Number.isNaN(rateNum)) return '—';
  return `$${rateNum.toFixed(2)}`;
}

const isMissingRequiredRate = (value) => value == null || Number.isNaN(value);

export function templateToConfig(template) {
  if (!template) return null;
  return {
    templateId: template.id,
    name: template.name ?? '',
    type: template.type,
    regularBillingRate: template.regularBillingRate ?? null,
    otBillingRate: template.otBillingRate ?? null,
    dtBillingRate: template.dtBillingRate ?? null,
    regularCostRate: template.regularCostRate ?? null,
    otCostRate: template.otCostRate ?? null,
    dtCostRate: template.dtCostRate ?? null,
    hourlyBillingRate: template.hourlyBillingRate ?? null,
    dailyBillingRate: template.dailyBillingRate ?? null,
    weeklyBillingRate: template.weeklyBillingRate ?? null,
    monthlyBillingRate: template.monthlyBillingRate ?? null,
    hourlyCostRate: template.hourlyCostRate ?? null,
    dailyCostRate: template.dailyCostRate ?? null,
    weeklyCostRate: template.weeklyCostRate ?? null,
    monthlyCostRate: template.monthlyCostRate ?? null,
    userIds: [...(template.userIds ?? [])],
    positionIds: [...(template.positionIds ?? [])],
    equipmentTypeIds: [...(template.equipmentTypeIds ?? [])],
    equipmentIds: [...(template.equipmentIds ?? [])],
  };
}

export function configToCreatePayload(config) {
  return {
    name: config.name?.trim(),
    type: config.type,
    regularBillingRate: config.regularBillingRate ?? null,
    otBillingRate: config.otBillingRate ?? null,
    dtBillingRate: config.dtBillingRate ?? null,
    regularCostRate: config.regularCostRate ?? null,
    otCostRate: config.otCostRate ?? null,
    dtCostRate: config.dtCostRate ?? null,
    hourlyBillingRate: config.hourlyBillingRate ?? null,
    dailyBillingRate: config.dailyBillingRate ?? null,
    weeklyBillingRate: config.weeklyBillingRate ?? null,
    monthlyBillingRate: config.monthlyBillingRate ?? null,
    hourlyCostRate: config.hourlyCostRate ?? null,
    dailyCostRate: config.dailyCostRate ?? null,
    weeklyCostRate: config.weeklyCostRate ?? null,
    monthlyCostRate: config.monthlyCostRate ?? null,
    sourceTemplateId: config.templateId,
    userIds: config.userIds ?? [],
    positionIds: config.positionIds ?? [],
    equipmentTypeIds: config.equipmentTypeIds ?? [],
    equipmentIds: config.equipmentIds ?? [],
  };
}

const getLabourCostRatesValidationError = (config) => {
  const hasRegular = !isNullOrUndefined(config.regularCostRate);
  const hasOt = !isNullOrUndefined(config.otCostRate);
  const hasDt = !isNullOrUndefined(config.dtCostRate);

  if (hasRegular || hasOt || hasDt) {
    if (!hasRegular) return 'Regular cost rate is required if any cost rate is provided.';
    if (!hasOt) return 'Overtime cost rate is required if any cost rate is provided.';
    if (!hasDt) return 'Double time cost rate is required if any cost rate is provided.';
  }

  return null;
};

const getConfigNameValidationError = (config, seenNames) => {
  const trimmedName = config.name?.trim();
  if (!trimmedName) return 'Name is required for each rate.';
  if (seenNames.has(trimmedName)) {
    return `Duplicate rate name in this request: "${trimmedName}"`;
  }
  seenNames.add(trimmedName);
  return null;
};

/* Needed to highlight/expand the panel with the error */
export function getConfigureValidationError(configsByTemplateId, selectedRowKeys) {
  const seenNames = new Set();

  for (const templateId of selectedRowKeys) {
    const config = configsByTemplateId[templateId];
    if (!config) return { message: 'Missing configuration for a selected template.', templateId };

    const nameError = getConfigNameValidationError(config, seenNames);
    if (nameError) return { message: nameError, templateId };

    if (config.type === 'labour') {
      const labourError = getLabourCostRatesValidationError(config);
      if (labourError) return { message: labourError, templateId };
    }
  }

  return null;
}

